'use client'

import { GoogleTagManager, sendGTMEvent } from '@next/third-parties/google'
import { algoliaPrimaryIndexName } from '@repo/algolia/config'
import {
  AddedToCartObjectIDs,
  AddedToCartObjectIDsAfterSearch,
  ALGOLIA_EVENTS,
  algoliaInsightsClient,
  ClickedObjectIDsAfterSearch,
  InsightsEvents,
  ViewedObjectIDs,
} from '@repo/algolia/insights'
import { saveAlgoliaQueryInfo } from '@repo/algolia/query'
import { useGetCookie } from '@repo/cookie/client'
import { COOKIE_USER_ID } from '@repo/cookie/config'
import { consentedTo, ConsentGroup } from '@repo/cookie/consent'
import { elevarScript } from '@repo/elevar/config'
import { ELEVAR_DATA_LAYER_EVENTS } from '@repo/elevar/events'
import { mapCartToElevarUserData } from '@repo/elevar/mapper'
import {
  ElevarAddToCartEventData,
  ElevarRemoveFromCartEventData,
  ElevarSelectItemEventData,
  ElevarSubscribeEventData,
  ElevarUserDataEventData,
  ElevarUserProperties,
  ElevarViewCartEventData,
  ElevarViewItemEventData,
  ElevarViewItemListEventData,
  ElevarViewSearchResultsEventData,
} from '@repo/elevar/types'
import { getFromStorage, LOCAL_STORAGE_EMAIL_UNVERIFIED } from '@repo/storage/service'
import { default as useIdleObserver } from '@repo/ui/hooks/useIdleObserver'
import { chunk } from '@repo/utils/array'
import { createHash } from '@repo/utils/hash.client'
import { usePathname } from 'next/navigation'
import Script from 'next/script'
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef } from 'react'
import { PageDto } from '../types/page.types'
import { useCart } from './CartProvider'

interface TelemetryContextType {
  userId: string | undefined
  trackElevarUserDataEvent: (eventData: Omit<ElevarUserDataEventData, 'user_properties'>) => void
  trackElevarViewItemListEvent: (eventData: Omit<ElevarViewItemListEventData, 'user_properties'>) => void
  trackElevarSelectItemEvent: (eventData: Omit<ElevarSelectItemEventData, 'user_properties'>) => void
  trackElevarViewItemEvent: (eventData: Omit<ElevarViewItemEventData, 'user_properties'>) => void
  trackElevarViewCartEvent: (eventData: Omit<ElevarViewCartEventData, 'user_properties'>) => void
  trackElevarAddToCartEvent: (eventData: Omit<ElevarAddToCartEventData, 'user_properties'>) => void
  trackElevarRemoveFromCartEvent: (eventData: Omit<ElevarRemoveFromCartEventData, 'user_properties'>) => void
  trackElevarViewSearchResultsEvent: (eventData: Omit<ElevarViewSearchResultsEventData, 'user_properties'>) => void
  trackElevarSubscribeEvent: (eventData: ElevarSubscribeEventData) => void
  trackAlgoliaViewObjectIDsEvent: (event: Omit<ViewedObjectIDs, 'userToken' | 'index' | 'eventType'>) => void
  trackAlgoliaClickedObjectIDAfterSearchEvent: (event: Omit<ClickedObjectIDsAfterSearch, 'userToken' | 'index' | 'eventType'>) => void
  trackAlgoliaAddToCartEvent: (event: Omit<AddedToCartObjectIDs, 'userToken' | 'index' | 'eventType' | 'eventSubtype'>) => void
  trackAlgoliaAddToCartAfterSearchEvent: (event: Omit<AddedToCartObjectIDsAfterSearch, 'userToken' | 'index' | 'eventType' | 'eventSubtype'>) => void
}

const TelemetryContext = createContext<TelemetryContextType | undefined>(undefined)

export const TelemetryProvider = ({ children, pageDto }: { children: ReactNode; pageDto: PageDto }) => {
  const { cart } = useCart()
  const pathname = usePathname()
  const getCookie = useGetCookie()
  const userId = getCookie(COOKIE_USER_ID)
  const gtmId = process.env.NEXT_PUBLIC_GTM_ID
  const algoliaIndex = algoliaPrimaryIndexName(pageDto.params.locale)
  const isDevelopmentEnvironment = process.env.NODE_ENV === 'development'

  // Track Elevar events and a hash of their payload, as to prevent sending duplicate events (due to re-renders of the page, due to Sanity Live for instance).
  const cartHash = useRef<string>(null)
  const lastTrackedElevarEvents = useRef<Record<string, string>>({})

  const trackElevarAndGTMEvent = useCallback(async (event: string | ELEVAR_DATA_LAYER_EVENTS, eventData: Record<string, any> = {}) => {
    const isElevarEvent = event.startsWith('dl_')
    const hash = isElevarEvent ? await createHash(eventData) : undefined

    // Deduplicate Elevar events.
    if (isElevarEvent && lastTrackedElevarEvents.current?.[event] === hash) {
      if (process.env.NODE_ENV === 'development') {
        console.debug(`Skipping duplicate Elevar ${event} event.`)
      }

      return
    }

    if (isElevarEvent) {
      window.ElevarDataLayer = window.ElevarDataLayer ?? []
      window.ElevarDataLayer.push({ event, ...eventData })

      if (hash) {
        lastTrackedElevarEvents.current[event] = hash
      }
    } else {
      sendGTMEvent({ event, ...eventData })
    }

    if (process.env.NODE_ENV === 'development') {
      console.debug(`Tracked ${isElevarEvent ? 'Elevar' : 'GTM'} ${event} event.`, eventData)
    }
  }, [])

  const trackAlgoliaEvent = useCallback((events: InsightsEvents) => {
    try {
      if (consentedTo(ConsentGroup.ANALYTICS)) {
        // @see https://www.algolia.com/doc/rest-api/insights/#tag/events
        algoliaInsightsClient.pushEvents(events)
      }
    } catch (error: unknown) {
      // Silent failure.
      console.error(error)
    }
  }, [])

  const elevarUserProperties = useCallback((): Readonly<ElevarUserProperties> => {
    return {
      visitor_type: 'guest',
      customer_id: userId || undefined,
      customer_email: getFromStorage(LOCAL_STORAGE_EMAIL_UNVERIFIED) || undefined,
    }
  }, [userId])

  const trackElevarUserDataEvent = useCallback(
    (eventData: Omit<ElevarUserDataEventData, 'user_properties'>) => {
      trackElevarAndGTMEvent(ELEVAR_DATA_LAYER_EVENTS.DL_USER_DATA, {
        ...eventData,
        user_properties: elevarUserProperties(),
      })
    },
    [elevarUserProperties, trackElevarAndGTMEvent],
  )

  const trackElevarViewItemListEvent = useCallback(
    (eventData: Omit<ElevarViewItemListEventData, 'user_properties'>) => {
      trackElevarAndGTMEvent(ELEVAR_DATA_LAYER_EVENTS.DL_VIEW_ITEM_LIST, {
        ...eventData,
        user_properties: elevarUserProperties(),
      })
    },
    [elevarUserProperties, trackElevarAndGTMEvent],
  )

  const trackElevarSelectItemEvent = useCallback(
    (eventData: Omit<ElevarSelectItemEventData, 'user_properties'>) => {
      trackElevarAndGTMEvent(ELEVAR_DATA_LAYER_EVENTS.DL_SELECT_ITEM, {
        ...eventData,
        user_properties: elevarUserProperties(),
      })
    },
    [elevarUserProperties, trackElevarAndGTMEvent],
  )

  const trackElevarViewItemEvent = useCallback(
    (eventData: Omit<ElevarViewItemEventData, 'user_properties'>) => {
      trackElevarAndGTMEvent(ELEVAR_DATA_LAYER_EVENTS.DL_VIEW_ITEM, {
        ...eventData,
        user_properties: elevarUserProperties(),
      })
    },
    [elevarUserProperties, trackElevarAndGTMEvent],
  )

  const trackElevarViewCartEvent = useCallback(
    (eventData: Omit<ElevarViewCartEventData, 'user_properties'>) => {
      trackElevarAndGTMEvent(ELEVAR_DATA_LAYER_EVENTS.DL_VIEW_CART, {
        ...eventData,
        user_properties: elevarUserProperties(),
      })
    },
    [elevarUserProperties, trackElevarAndGTMEvent],
  )

  const trackElevarAddToCartEvent = useCallback(
    (eventData: Omit<ElevarAddToCartEventData, 'user_properties'>) => {
      trackElevarAndGTMEvent(ELEVAR_DATA_LAYER_EVENTS.DL_ADD_TO_CART, {
        ...eventData,
        user_properties: elevarUserProperties(),
      })
    },
    [elevarUserProperties, trackElevarAndGTMEvent],
  )

  const trackElevarRemoveFromCartEvent = useCallback(
    (eventData: Omit<ElevarRemoveFromCartEventData, 'user_properties'>) => {
      trackElevarAndGTMEvent(ELEVAR_DATA_LAYER_EVENTS.DL_REMOVE_FROM_CART, {
        ...eventData,
        user_properties: elevarUserProperties(),
      })
    },
    [elevarUserProperties, trackElevarAndGTMEvent],
  )

  const trackElevarViewSearchResultsEvent = useCallback(
    (eventData: Omit<ElevarViewSearchResultsEventData, 'user_properties'>) => {
      trackElevarAndGTMEvent(ELEVAR_DATA_LAYER_EVENTS.DL_VIEW_SEARCH_RESULTS, {
        ...eventData,
        user_properties: elevarUserProperties(),
      })
    },
    [elevarUserProperties, trackElevarAndGTMEvent],
  )

  const trackElevarSubscribeEvent = useCallback(
    (eventData: ElevarSubscribeEventData) => {
      trackElevarAndGTMEvent(ELEVAR_DATA_LAYER_EVENTS.DL_SUBSCRIBE, eventData)
    },
    [trackElevarAndGTMEvent],
  )

  const trackAlgoliaViewObjectIDsEvent = useCallback(
    (event: Omit<ViewedObjectIDs, 'userToken' | 'index' | 'eventType'>) => {
      // Algolia API allows maximum of 20 objectIDs.
      const objectIDChunks = chunk(event.objectIDs, 20)
      const base: ViewedObjectIDs = { ...event, index: algoliaIndex, eventType: 'view', userToken: userId || '' }

      trackAlgoliaEvent({
        events: objectIDChunks.map((chunk) => ({ ...base, objectIDs: chunk })),
      })
    },
    [algoliaIndex, trackAlgoliaEvent, userId],
  )

  const trackAlgoliaClickedObjectIDAfterSearchEvent = useCallback(
    (event: Omit<ClickedObjectIDsAfterSearch, 'userToken' | 'index' | 'eventType'>) => {
      trackAlgoliaEvent({
        events: [{ ...event, index: algoliaIndex, eventType: 'click', userToken: userId || '' }],
      })
    },
    [algoliaIndex, trackAlgoliaEvent, userId],
  )

  const trackAlgoliaAddToCartEvent = useCallback(
    (event: Omit<AddedToCartObjectIDs, 'userToken' | 'index' | 'eventType' | 'eventSubtype'>) => {
      trackAlgoliaEvent({
        events: [{ ...event, index: algoliaIndex, eventType: 'conversion', eventSubtype: 'addToCart', userToken: userId || '' }],
      })
    },
    [algoliaIndex, trackAlgoliaEvent, userId],
  )

  const trackAlgoliaAddToCartAfterSearchEvent = useCallback(
    (event: Omit<AddedToCartObjectIDsAfterSearch, 'userToken' | 'index' | 'eventType' | 'eventSubtype'>) => {
      trackAlgoliaEvent({
        events: [{ ...event, index: algoliaIndex, eventType: 'conversion', eventSubtype: 'addToCart', userToken: userId || '' }],
      })
    },
    [algoliaIndex, trackAlgoliaEvent, userId],
  )

  // Elevar custom user ID.
  // @see https://docs.getelevar.com/docs/how-to-pass-my-own-user-identifier
  if (typeof window !== 'undefined') {
    window.ElevarUserIdFn = () => {
      return userId
    }
  }

  // Inform Elevar about route changes.
  // @see https://docs.getelevar.com/docs/refresh-utms-cookies-and-click-ids-known-to-elevar
  useEffect(() => {
    window?.ElevarInvalidateContext?.()

    // Clear tracking of past events.
    lastTrackedElevarEvents.current = {}
  }, [pathname, trackElevarUserDataEvent])

  // Cart changes should reset Elevar event de-duplication.
  useEffect(() => {
    const hash = JSON.stringify(cart)

    if (hash !== cartHash.current) {
      lastTrackedElevarEvents.current = {}
      cartHash.current = hash
    }
  }, [cart])

  // Elevar user data event (when browser is idle, or after
  // 1 sec. if browser does not support idle callbacks).
  useIdleObserver(
    () => {
      if (cart) {
        trackElevarUserDataEvent(mapCartToElevarUserData(cart, pageDto.params.locale))
      }
    },
    { timeout: 1000 },
    [pathname, cart],
  )

  // Telemetry via data attributes so that we can use more server-side rendering.
  useEffect(() => {
    const handleClick = (event: MouseEvent): void => {
      const target = event.target as HTMLElement
      const element = target.closest<HTMLElement>('a, button')

      if (!element) {
        return
      }

      const elevarData = element.closest<HTMLElement>(`[data-telemetry-event="${ELEVAR_DATA_LAYER_EVENTS.DL_SELECT_ITEM}"]`)?.dataset.telemetryEventData
      const algoliaData = element.closest<HTMLElement>(`[data-telemetry-event="${ALGOLIA_EVENTS.CLICK_OBJECT_IDS_AFTER_SEARCH}"]`)?.dataset.telemetryEventData

      if (!elevarData && !algoliaData) {
        return
      }

      runWhenIdle(() => {
        if (elevarData) {
          trackElevarSelectItemEvent(JSON.parse(elevarData) as Omit<ElevarSelectItemEventData, 'user_properties'>)
        }

        if (algoliaData) {
          const eventData = JSON.parse(algoliaData) as Omit<ClickedObjectIDsAfterSearch, 'userToken' | 'index' | 'eventType'>

          trackAlgoliaClickedObjectIDAfterSearchEvent(eventData)

          if (consentedTo(ConsentGroup.ANALYTICS)) {
            saveAlgoliaQueryInfo(eventData.queryID, eventData.objectIDs)
          }
        }
      })
    }

    document.addEventListener('click', handleClick)

    return (): void => {
      document.removeEventListener('click', handleClick)
    }
  }, [trackAlgoliaClickedObjectIDAfterSearchEvent, trackElevarSelectItemEvent])

  const forceLoad: boolean = false
  const loadThirdPartyScripts = (gtmId && !isDevelopmentEnvironment) || (isDevelopmentEnvironment && forceLoad)

  return (
    <TelemetryContext.Provider
      value={{
        userId,
        trackElevarUserDataEvent,
        trackElevarViewItemListEvent,
        trackElevarSelectItemEvent,
        trackElevarViewItemEvent,
        trackElevarViewCartEvent,
        trackElevarAddToCartEvent,
        trackElevarRemoveFromCartEvent,
        trackElevarViewSearchResultsEvent,
        trackElevarSubscribeEvent,
        trackAlgoliaViewObjectIDsEvent,
        trackAlgoliaClickedObjectIDAfterSearchEvent,
        trackAlgoliaAddToCartEvent,
        trackAlgoliaAddToCartAfterSearchEvent,
      }}
    >
      {loadThirdPartyScripts && gtmId && (
        <>
          <GoogleTagManager gtmId={gtmId} />
          <Script id='elevar' type='module' strategy='afterInteractive' dangerouslySetInnerHTML={{ __html: elevarScript(pageDto.shopifyInstance) }} />
        </>
      )}
      {children}
    </TelemetryContext.Provider>
  )
}

const runWhenIdle = (cb: () => void, timeout = 1000): void => {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(cb, { timeout })
  } else {
    setTimeout(cb, timeout)
  }
}

export const useTelemetry = () => {
  const context = useContext(TelemetryContext)

  if (!context) {
    throw new Error('useTelemetry must be used within a TelemetryProvider')
  }

  return context
}
